import { FC, useEffect, useState } from 'react';
import { Switch } from '@chakra-ui/switch';
import { Box, Heading, HStack, Text, VStack } from '@chakra-ui/layout';
import { Select } from '@chakra-ui/react';
import CrucibleScene from 'helpers/nft/crucibleScene/crucibleScene';
import crucibleSceneStore from 'helpers/nft/crucibleSceneStore';
import { isMobile } from 'react-device-detect';

type Props = {
  scene: CrucibleScene;
  handleSetGraphicsOptions: () => void;
};

const GraphicsOptions: FC<Props> = ({ scene, handleSetGraphicsOptions }) => {
  const [dynamicLights, setDynamicLights] = useState(
    scene.options.dynamicLights
  );
  const [shadows, setShadows] = useState(scene.options.shadows);
  const [antialias, setAntiAlias] = useState(scene.options.antialias);
  const [bloom, setBloom] = useState(scene.options.bloom);
  const [quality, setQuality] = useState(scene.options.textureQuality);

  const reloadActiveScene = (value: string) => {
    setQuality(value);
    crucibleSceneStore.reloadScene({ textureQuality: value });
  };

  useEffect(() => {
    scene.ee.on('settings', (e) => {
      if (e.type === 'textureQuality') {
        setQuality(scene.options.textureQuality);
      }

      if (e.type === 'lights') {
        setDynamicLights(scene.options.dynamicLights);
      }

      if (e.type === 'shadows') {
        setShadows(scene.options.shadows);
      }

      if (e.type === 'antialias') {
        setAntiAlias(scene.options.antialias);
      }

      if (e.type === 'bloom') {
        setBloom(scene.options.bloom);
      }
    });
  }, [scene]);

  return (
    <Box
      m={4}
      p={[4, 4, 8]}
      top={16}
      right={0}
      overflowY='auto'
      maxWidth={'380px'}
      borderRadius='md'
      bg='whiteAlpha.100'
      position='absolute'
      backdropFilter='blur(100px)'
      zIndex={1}
      textAlign='left'
      sx={{
        '::-webkit-scrollbar': {
          width: '20px'
        },
        '::-webkit-scrollbar-track': {
          backgroundColor: 'transparent'
        },
        '::-webkit-scrollbar-thumb': {
          backgroundColor: 'whiteAlpha.300',
          borderRadius: '20px',
          border: '6px solid transparent',
          backgroundClip: 'content-box'
        }
      }}
    >
      <Heading size='md' mb={3}>
        Graphics options
      </Heading>
      <VStack>
        <HStack justifyContent='space-between' alignItems='center' width='100%'>
          <Text>Bloom (Glow)</Text>
          <Switch
            isChecked={bloom}
            onChange={(e) =>
              scene.changeGraphicsSettings({
                type: 'bloom',
                value: e.target.checked
              })
            }
          />
        </HStack>
        <HStack justifyContent='space-between' alignItems='center' width='100%'>
          <Text>Dynamic Lights</Text>
          <Switch
            isChecked={dynamicLights}
            onChange={(e) =>
              scene.changeGraphicsSettings({
                type: 'lights',
                value: e.target.checked
              })
            }
          />
        </HStack>
        <HStack justifyContent='space-between' alignItems='center' width='100%'>
          <Text>Dynamic Shadows</Text>
          <Switch
            isChecked={shadows}
            onChange={(e) =>
              scene.changeGraphicsSettings({
                type: 'shadows',
                value: e.target.checked
              })
            }
          />
        </HStack>
        <HStack justifyContent='space-between' alignItems='center' width='100%'>
          <Text>Anti-Aliasing</Text>
          <Switch
            isChecked={antialias}
            onChange={(e) =>
              scene.changeGraphicsSettings({
                type: 'antialias',
                value: e.target.checked
              })
            }
          />
        </HStack>

        <HStack justifyContent='space-between' alignItems='center' width='100%'>
          <Text>Textures</Text>
          <Select
            size='sm'
            value={quality}
            onChange={(e) => {
              reloadActiveScene(e.target.value);
              handleSetGraphicsOptions();
            }}
          >
            <option value='low'>Low</option>
            <option value='medium'>Medium</option>
            <option value='high' disabled={isMobile}>
              High {isMobile ? '(Desktop Only)' : ''}
            </option>
          </Select>
        </HStack>
      </VStack>
    </Box>
  );
};

export default GraphicsOptions;
